import { MultiLineString, Point, Polygon } from 'geojson';
import { Dispatch } from 'react';
import { IStation, IVersion, IZoneArea, IZoneReduced } from './common';

export interface IContainer {
  pdf: string;
  route_name: string;
  container_id: string;
  container_name: string;
}

export interface IPlan {
  _id: string;
  plan_name: string;
  session_id: string;
  routes: string;
  status: string;
  query: {
    kmeans: IFormKMeans;
    station: IStation;
  };
  geometry: Array<GeoJSON.FeatureCollection<Point>>;
  convex: Array<GeoJSON.FeatureCollection<Polygon>>;
  polylines: Array<Array<GeoJSON.FeatureCollection<MultiLineString | Point>>>;
  areas: Array<IZoneReduced>;
  containers: Array<IContainer>;
  createdAt: string;
}

export interface Geometry {
  type: string;
  coordinates: [number, number] | [[number, number]] | [[[number, number]]];
  properties: any;
}

export interface RouteOrder {
  tracking_id: number;
  internal_key: string;
  delivery_order: number;
  geometry: Geometry;
}

export interface OutRoute {
  id: string;
  name: string;
  orders: Array<RouteOrder>;
  color: string;
}

export interface Route {
  id: string;
  vehicle_type: string;
  name: string;
  time: number;
  distance: number;
  capacity: number;
  orders: Array<RouteOrder>;
  color: string;
  container: string;
  container_name: string;
  disabled?: boolean;
}

export interface InductionRoute {
  id: string;
  vehicle_type: string;
  name: string;
  time: number;
  distance: number;
  capacity: number;
  orders: Array<RouteOrder>;
  color: string;
  plan_id: string;
  container: string;
  container_name: string;
  disabled?: boolean;
}

export enum SimulationStatus {
  pending = 'pending',
  accepted = 'accepted',
  rejected = 'rejected',
}

export interface Containerized {
  route: string;
  tracking_id: number;
}

export interface Simulation {
  plan_id: string;
  plan_name: string;
  prefix: string;
  station: string;
  country: string;
  mode: string;
  sizes: Array<string>;
  algorithm: string;
  universe: Array<string>;
  simulation_time: number;
  route_time: number;
  fusion: number;
  preset: string;
  routes: Array<Route>;
  out_routes: Array<OutRoute>;
  areas: Array<IZoneArea>;
  polylines?: Array<Array<GeoJSON.FeatureCollection<MultiLineString | Point>>>;
  vehicles_used?: any;
  containers: Array<any>;
  induct_accept: SimulationStatus;
  containerized: Array<Containerized>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface IPlanResumen {
  zoneId: string;
  zone: string;
  route: string;
  stops: number;
  total: number;
  fixed: number;
  time?: string;
  distance?: string;
  vehicle?: string;
}

export enum algorithms {
  k_means = 'k_means',
  or_tools = 'or_tools',
}

export enum order_status {
  station = 'station',
  on_the_way = 'on_the_way',
  sorting = 'sorting',
  on_return = 'on_return',
}

export enum sizes {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

export enum sizes_enum {
  xs = 'xs',
  sm = 's',
  md = 'm',
  lg = 'l',
  xl = 'xl',
}

export enum ReducerTypes {
  Kmeans = 'kmeans',
  OrTools = 'ortools',
}

export enum vehicle_types {
  motorcycle = 'motorcycle',
  auto = 'auto',
  small_van = 'small_van',
  large_van = 'large_van',
  same_day = 'same_day',
  electric_van = 'electric_van',
  x = 'x',
}

export interface IVehicleCapacity {
  type: vehicle_types;
  amount: number;
  capacity: number;
}

export interface IVehiclesCapacities {
  [vehicle_types.motorcycle]: number;
  [vehicle_types.auto]: number;
  [vehicle_types.small_van]: number;
  [vehicle_types.large_van]: number;
  [vehicle_types.same_day]: number;
  [vehicle_types.electric_van]: number;
}

export interface IVehiclesCapacitiesShort {
  [vehicle_types.motorcycle]: number;
  [vehicle_types.auto]: number;
  [vehicle_types.small_van]: number;
  [vehicle_types.large_van]: number;
}

export interface IFormKMeans {
  with_sp: boolean;
  spr_min: number;
  spr_max: number;
  sizes: Array<sizes>;
  zones: Array<string>;
  vehicle: IVehicleCapacity;
  status: Array<order_status>;
}

export interface IFormOrTools {
  time: number;
  route_time: number;
  type_route: string;
  sizes: Array<sizes>;
  zones: Array<string>;
  status: Array<order_status>;
  vehicles: IVehiclesCapacities;
}

export interface IForm {
  session_id?: string;
  prefix: string;
  algorithm: algorithms | undefined;

  excel: boolean;
  exit_on_station: boolean;
  file: File | null;

  plan_name: string;

  differential: boolean;

  kmeans?: IFormKMeans;
  ortools?: IFormOrTools;
  version?: IVersion | undefined;
  station: IStation | undefined;
  original_id?: string;
  days?: number | undefined;
  is_automatic_route?: boolean | null;
  delivery_types: Array<any>;
  pickup_attempts: number;
}

type Dispatcher = Dispatch<{
  type?: ReducerTypes;
  payload?: Partial<IForm | IFormKMeans | IFormOrTools>;
}>;

export interface IMinimalForm {
  form: IForm;
  dispatch: Dispatcher;
}

export type IFormReducer = [IForm, Dispatcher];

export interface IResponsePrepare {
  session_id: string;
  outside: number;
  orders: number;
  planed: number;
}

export interface IProperties {
  centroid: [number, number];
  counter: string;
  fixed: boolean;
  h3: string;
  route_name: string;
  idempotence_id: string;
  point: number;
  ref: string;
  sequence: string;
  stop: number;
  zone: string;
}

export enum calculation_mode {
  automatic = 'automatic',
  manual = 'manual',
}

export enum universe_mode {
  station = 'station',
  station_return = 'station_return',
  linehaul = 'linehaul',
  linehaul_return = 'linehaul_return',
}

export interface IKMeansRecords {
  _id: string;
  prefix: string;
  plan_name: string;
  status: 'pending' | 'finished' | 'processing' | 'approved';
  createdAt: string;
  country: string;
  orders: string;
  circuit_categories: Array<string>;
  created_by: string;
  created_at: string;
  session_id: string;
  count_routes: number;
  pre_routes: number;
  station_code: string;
  api: any;
  deliveries_type: Array<string>;
}
